import React, { useEffect, useCallback, useState, useMemo } from 'react'
import useParams from 'core/hooks/useParams'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import { getSshKeysForUser, createSshKeyForUser, deleteSshKeyForUser } from './actions'
import ModalForm from 'core/elements/modal/ModalForm'
import TextField from 'core/components/validatedForm/TextField'
import Text from 'core/elements/Text'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import PicklistField from 'core/components/validatedForm/DropdownField'
import UsersPicklist from 'account/components/userManagement/users/UsersPicklist'
import Button from 'core/elements/button'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import Alert from 'core/components/Alert'

const useStyles = makeStyles<Theme>((theme) => ({
  info: {
    marginBottom: '12px',
  },
  content: {
    display: 'grid',
    gap: 16,
  },
  table: {
    display: 'grid',
    gridTemplateColumns: 'auto auto max-content',
    alignItems: 'center',
    gap: 16,
  },
  buttons: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content auto',
    gap: 16,
  },
}))

export default function ManageUserSshKeysModal({ onClose }) {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [sshKeys, setSshKeys] = useState([])
  const [createError, setCreateError] = useState('')
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const defaultParams = {
    name: '',
    publicKey: '',
    user: '',
    keyToDelete: '',
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const refreshKeys = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getSshKeysForUser({ userId: params.user })
      setSshKeys(response)
    } catch (e) {
      console.log(e)
    } finally {
      setLoading(false)
    }
  }, [params.user])

  useEffect(() => {
    if (params.user) {
      refreshKeys()
    }
  }, [refreshKeys])

  const createKey = useCallback(async () => {
    const body = {
      keypair: {
        name: params.name,
        public_key: params.publicKey,
        user_id: params.user,
      },
    }
    try {
      await createSshKeyForUser({ body })
    } catch (e) {
      setCreateError(JSON.stringify(e.response?.data))
      return
    }
    refreshKeys()
    setShowAdd(false)
  }, [params, refreshKeys])

  const handleClose = () => {
    setParams(defaultParams)
    onClose()
  }

  const handleConfirm = useCallback(async () => {
    await deleteSshKeyForUser({ name: params.keyToDelete, userId: params.user })
    refreshKeys()
    setShowConfirmationDialog(false)
  }, [params, refreshKeys])

  return (
    <>
      <ModalForm
        closeText="Close"
        title={`Manage User SSH Keys`}
        onClose={handleClose}
        submitting={submitting}
        open
      >
        {!showAdd && (
          <div className={classes.content}>
            <div className={classes.info}>
              <Text variant="body2">
                Select a user to create and manage SSH keys for that user.
              </Text>
            </div>
            <PicklistField
              DropdownComponent={UsersPicklist}
              id="user"
              onChange={getParamsUpdater('user')}
              value={params.user}
              label="User"
              info="Optionally select a user that the SSH key belongs to. If this is left blank, then the SSH key will assigned to yourself. You will not see SSH keys belonging to other users."
            />
            {params.user && (
              <FormFieldSection title={loading ? 'Loading SSH Keys...' : 'SSH Keys'}>
                {!loading && (
                  <>
                    {!!sshKeys.length ? (
                      <div className={classes.table}>
                        <Text variant="caption1">Name</Text>
                        <Text variant="caption1">Fingerprint</Text>
                        <div />
                        {sshKeys.map((sshKey) => (
                          <React.Fragment key={sshKey.name}>
                            <Text variant="body2">{sshKey.name}</Text>
                            <Text variant="body2">{sshKey.fingerprint}</Text>
                            <Button
                              variant="primary"
                              icon="trash-alt"
                              onClick={() => {
                                updateParams({ keyToDelete: sshKey.name })
                                setShowConfirmationDialog(true)
                              }}
                            >
                              Delete
                            </Button>
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      <Text variant="body2">This user has no SSH keys</Text>
                    )}
                    <div>
                      <Button variant="primary" onClick={() => setShowAdd(true)}>
                        + Add SSH Key
                      </Button>
                    </div>
                  </>
                )}
              </FormFieldSection>
            )}
          </div>
        )}
        {showAdd && (
          <FormFieldSection title="SSH Key Info">
            <TextField
              id="name"
              label="Name"
              onChange={getParamsUpdater('name')}
              value={params.name}
              required
            />
            <TextField
              id="publicKey"
              label="Public Key Content"
              onChange={getParamsUpdater('publicKey')}
              value={params.publicKey}
              info={'Public SSH keys are normally located at ~/.ssh/id_rsa.pub.'}
              required
            />
            <div className={classes.buttons}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowAdd(false)
                  setCreateError('')
                  updateParams({ name: '', publicKey: '' })
                }}
              >
                Back
              </Button>
              <Button variant="primary" onClick={createKey}>
                Confirm
              </Button>
              {createError && <Alert variant="error" message={createError} />}
            </div>
          </FormFieldSection>
        )}
      </ModalForm>
      {showConfirmationDialog && (
        <ConfirmationDialog
          open
          title="Delete SSH Key?"
          text="Are you sure you want to delete this SSH key?"
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmationDialog(false)}
        />
      )}
    </>
  )
}
